import PropTypes from 'prop-types';
import styled, { createGlobalStyle, css } from 'styled-components';
import React, { useRef, useEffect } from 'react';
import SelectAntd from 'antd/es/select';
import 'antd/es/select/style/css';
import { theme } from '../utilsClient/cssHelpers';
import {
  resetStyles,
  lineHeights,
  minHeights,
  fontSizes,
  transitionStyles,
  activeStyles,
} from './styles/form-controls';

export const arrowSizes = {
  small: css`
    font-size: 10px;
    width: 10px;
    height: 10px;
    margin-top: -4px;
    right: 13px;
  `,
  default: css``,
  large: css``,
};

const styles = css`
  &&&&& {
    &,
    .ant-select-arrow {
      color: ${theme('color.onSurface')};
    }

    .ant-select-arrow {
      ${(props) => arrowSizes[props.size]};
    }

    .ant-select-selection-placeholder,
    &.ant-select-single .ant-select-selector .ant-select-selection-item {
      ${(props) => lineHeights[props.size]};
      ${(props) => fontSizes[props.size]};
    }
    .ant-select-selection-placeholder {
      color: #9ca3af;
      font-size: 0.875rem;
    }
    &.ant-select-multiple .ant-select-selector .ant-select-selection-item {
      border-radius: ${theme('border.2')};
      background-color: ${theme('color.underlay')};
      color: ${theme('color.onUnderlay')};
      /* TODO: Should be in sync with theme */
      border: none;
    }
    /* Useful for E2E tests where the selection item covers the actual <input /> element */
    &.ant-select-single .ant-select-selector .ant-select-selection-item {
      pointer-events: none;
    }

    /* Double selector hack to overwrite Ant Design specificity */
    .ant-select-selector.ant-select-selector {
      ${resetStyles};

      background-color: ${theme('color.surface')};
      border-radius: ${theme('border.radius.2')};
      border: 1px solid var(--color-input-border);
      padding-right: ${theme('space.2')};
      padding-left: ${theme('space.2')};

      ${transitionStyles};

      /* Modifiers */
      ${(props) => minHeights[props.size]};
    }

    .ant-select-selection-item {
      ${(props) => fontSizes[props.size]};
    }

    /* Makes select menu clear button always visible */
    .ant-select-clear {
      opacity: 1;
      background-color: ${theme('color.surface')};
      color: ${theme('color.onSurface')};

      .anticon {
        vertical-align: initial;
      }
    }

    &.ant-select-disabled {
      .ant-select-selector {
        background-color: ${theme('color.disabled')};
      }

      .ant-select-selector,
      .ant-select-arrow {
        color: ${theme('color.onDisabled')};
      }
    }

    &:not(.ant-select-disabled):hover,
    &.ant-select-focused:not(.ant-select-disabled).ant-select-single:not(.ant-select-customize-input) {
      .ant-select-selector {
        ${activeStyles};
      }
    }
  }
`;

const GlobalStyle = createGlobalStyle`
  .ant-select-dropdown {
    &&& {
      background-color: ${theme('color.surface')};
    }
  }

  .ant-select-item-option {
    &&& {
      &:not(.ant-select-item-option-disabled) {
        color: ${theme('color.onSurface')};
      }

      &-selected {
        color: ${theme('color.onSurface')};
      }

      &-selected,
      &-active,
      &:hover:not(.ant-select-item-option-disabled) {
        background-color: ${theme('color.hover')};
      }
    }
  }

  .ant-select-item-group,
  .ant-empty {
    && {
      color: ${theme('color.onSurface')};
    }
  }
`;

const ComponentContainer = styled.div`
  display: inline-block;
  ${(props) => !props.compact && 'width: 100%'};
`;

const Component = styled(SelectAntd)`
  ${(props) => !props.styles.isUnstyled && styles}
`;

const StyledSelect = ({
  // eslint-disable-next-line react/prop-types
  showSearch,
  compact,
  size,
  isUnstyled,
  ...rest
}) => {
  const ref = useRef(null);

  useEffect(() => {
    // Ant Design select comes with autocomplete="off" for the search input
    // but it doesn't seem to work in Chrome
    // adding a dummy value to autocomplete attr seems to fix the issue
    // as per https://stackoverflow.com/a/38961567
    if (showSearch) {
      const searchInput = ref.current.querySelector(
        '.ant-select-selection-search-input',
      );

      if (searchInput) {
        searchInput.setAttribute('autocomplete', 'off');
      }
    }
  }, [showSearch]);

  const handleGetPopupContainer = () => {
    const dialog = ref.current && ref.current.closest('dialog');

    if (dialog) {
      return dialog;
    }

    return document.body;
  };

  return (
    <ComponentContainer ref={ref} compact={compact}>
      <GlobalStyle />
      <Component
        showSearch={showSearch}
        getPopupContainer={handleGetPopupContainer}
        size={size}
        styles={{
          isUnstyled,
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
    </ComponentContainer>
  );
};

StyledSelect.propTypes = {
  showSearch: PropTypes.bool,
  compact: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'default', 'large']),
  isUnstyled: PropTypes.bool,
};

StyledSelect.defaultProps = {
  showSearch: false,
  compact: false,
  size: 'default',
  isUnstyled: false,
};

export default SelectAntd;

export { StyledSelect };
